import React from "react";
import PropTypes from "prop-types";
import "./BlogPage.css"; // Re-added this import
import blog2Image from "../assets/blog2.jpg"; // 1. Import the image here
import blog3Image from "../assets/blog3.jpg"; // 1. Import the image here
import blog4Image from "../assets/blog4.jpg"; // 1. Import the image here

// ---- reveal-on-scroll (unchanged) ----
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current || inView) return;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

// --------- small helpers ----------
const isNonEmpty = (v) =>
  Array.isArray(v) ? v.length > 0 : Boolean(v);

const Paragraphs = ({ paragraphs }) =>
  isNonEmpty(paragraphs) ? paragraphs.map((p, i) => <p key={i}>{p}</p>) : null;

// Story Block Component with Read More functionality
const StoryBlock = ({ block }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const paragraphs = block.copy ? block.copy.split('\n\n') : [];
  const previewParagraphs = paragraphs.slice(0, 3);
  const hasMore = paragraphs.length > 3;

  // Simple component for an eyebrow/kicker
  const ContentEyebrow = ({ text }) => {
    if (!text) return null;
    return <h5 className="foundation__contentEyebrow">{text}</h5>;
  }

  return (
    <div className={`foundation__storyBlock ${block.flip ? 'foundation__storyBlock--flip' : ''}`}>
      {block.image && (
        <div className="foundation__contentImage-wrap foundation__contentImage-wrap--floated">
          <img 
            src={block.image} 
            alt={block.imageAlt || block.title || ''} 
            className="foundation__contentImage"
            loading="lazy"
          />
        </div>
      )}

      <ContentEyebrow text={block.eyebrow} />

      {block.title && <h4 className="foundation__sectionTitle" style={{ fontSize: "1.25rem" }}>{block.title}</h4>}
      
      {block.copy && (
        <>
          {(isExpanded ? paragraphs : previewParagraphs).map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
          {hasMore && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="foundation__readMoreBtn"
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1.5rem',
                backgroundColor: '#3244ba',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2533a1'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3244ba'}
            >
              {isExpanded ? 'Show Less' : 'Read More'}
            </button>
          )}
        </>
      )}
      
      {Array.isArray(block.points) && block.points.length > 0 && (
        <ul className="foundation__organizationsList" style={{ marginTop: '1rem' }}>
          {block.points.map((li, j) => <li key={j}>{li}</li>)}
        </ul>
      )}
    </div>
  );
};

// Removed the PageStyles component that contained the inline CSS

// ===================================
//   Foundation-style General Blog Page
// ===================================
export default function BlogPage({ data, carouselPhotos }) { // MODIFIED: Removed relatedPosts prop
  // ---- MOVE ALL HOOKS TO THE TOP, BEFORE ANY CONDITIONAL LOGIC ----
  const [titleRef, titleIn] = useReveal();
  const [heroSectionRef, heroSectionIn] = useReveal();
  const [contentRef, contentIn] = useReveal();
  const [carouselRef, carouselIn] = useReveal();

  // ---- NOW you can do the early return ----
  if (!data) return null;

  // ---- derive sections from your existing data ----
  // Quotes come from blocks that have `quote`
  const quoteBlocks = (data.blocks || []).filter((b) => b.quote);
  // Content blocks (no quote) - all rendered with same layout to support flip
  const contentBlocks = (data.blocks || []).filter((b) => !b.quote);

  // Two-column: map first two content blocks if present (kept for backward compatibility)
  const leftBlock = null; // Disabled - now using extraBlocks for all content
  const rightBlock = null; // Disabled - now using extraBlocks for all content
  const extraBlocks = contentBlocks; // All content blocks use the same horizontal layout

  // Auto-detect "donors" style section: lots of names (points > 10) or title/eyebrow mention
  const donorsBlock =
    (data.blocks || []).find(
      (b) =>
        (Array.isArray(b.points) && b.points.length > 10) ||
        /donor|donateur|donateurs|donors/i.test(`${b.title || ""} ${b.eyebrow || ""}`)
    ) || null;

  // Dates for related cards
  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  // ========================================================
  // Hardcoded related posts, as requested.
  // ========================================================
  const relatedPosts = [
    {
      title: "Breaking Boundaries: Our Journey Toward Nationwide Presence",
      slug: "breaking-boundaries",
      image: blog2Image, // 2. Use the imported variable here
      date: "2025-09-15",
      readTime: 6
    },
    {
      title: "Building the Next Generation of Security Professionals",
      slug: "building-the-next-generation",
      image: blog4Image,
      date: "2025-08-18",
      readTime: 7
    },
    {
      title: "Real Problems, Real Solutions: Stories from the Field",
      slug: "stories-from-the-field",
      image: blog3Image,
      date: "2025-09-01",
      readTime: 5
    }
  ];
  // ========================================================

  // ---- guards to render conditionally ----
  const hasTitle = isNonEmpty(data.hero?.kicker) || isNonEmpty(data.hero?.title) || isNonEmpty(data.hero?.subtitle);
  const hasHeroCol =
    isNonEmpty(data.lead?.title) || isNonEmpty(data.hero?.bgImage) || isNonEmpty(data.lead?.copy);
  // Only show quotes for the foundation page
  const isFoundationPage = data.slug === "foundation";
  const hasQuotes = isFoundationPage && quoteBlocks.length > 0;
  const hasRelated = Array.isArray(relatedPosts) && relatedPosts.length > 0; // This will now use the hardcoded array
  const hasCarousel = Array.isArray(carouselPhotos) && carouselPhotos.length > 0;
  const hasContent = contentBlocks.length > 0;
  const hasDonors = Boolean(donorsBlock);

  // Simple component for a content image
  const ContentImage = ({ block }) => {
    if (!block.image) return null;
    return (
      <div className="foundation__contentImage-wrap">
        <img
          src={block.image}
          alt={block.imageAlt || block.title || ""}
          className="foundation__contentImage"
          loading="lazy"
        />
      </div>
    );
  };
  
  // Simple component for an eyebrow/kicker
  const ContentEyebrow = ({ text }) => {
    if (!text) return null;
    return <h5 className="foundation__contentEyebrow">{text}</h5>;
  }

  return (
    <section id="foundation" aria-labelledby="foundation-heading">
      {/* Removed <PageStyles /> call */}
      <div className="foundation__container">

        {/* ===== Title Section (unchanged) ===== */}
        {hasTitle && (
          <div
            ref={titleRef}
            className={`foundation__title reveal reveal--up ${titleIn ? "in" : ""}`}
          >
            {data.hero?.kicker && (
              <div className="foundation__badge reveal-item">{data.hero.kicker}</div>
            )}
            {(data.hero?.title || data.lead?.title) && (
              <h1 id="foundation-heading" className="foundation__mainTitle reveal-item">
                {data.hero?.title || data.lead?.title}
              </h1>
            )}
            {(data.hero?.subtitle || data.lead?.copy) && (
              <p className="foundation__subtitle reveal-item">
                {data.hero?.subtitle || data.lead?.copy}
              </p>
            )}
          </div>
        )}

        {/* ===== Hero / Quotes / Related ===== */}
        {(hasHeroCol || hasQuotes || hasRelated) && (
          <div
            ref={heroSectionRef}
            className={`foundation__heroSection reveal reveal--up ${heroSectionIn ? "in" : ""}`}
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fb 100%)",
              padding: "2.5rem",
              borderRadius: "12px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              marginBottom: "3rem"
            }}
          >
            {/* Left: hero image + about */}
            {hasHeroCol && (
              <div className="foundation__photoBox reveal-item" style={{ transitionDelay: "80ms", maxWidth: "100%" }}>
                {data.hero?.bgImage && (
                  <div className="foundation__photoContainer">
                    <img
                      src={data.hero.bgImage}
                      alt={data.hero?.imageAlt || data.lead?.title || "Hero"}
                      className="foundation__photo"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Middle: Stories info (replaces quotes) or quotes & CTA for foundation page */}
            {(hasQuotes || data.footerCta || hasHeroCol) && (
              <div className="foundation__quotesColumn reveal-item" style={{ transitionDelay: "160ms", paddingTop: "0" }}>
                {hasQuotes && (
                  <div className="foundation__quotes">
                    {quoteBlocks.slice(0, 6).map((q, i) => (
                      <blockquote key={i} className="foundation__quote">
                        <p>"{q.quote}"</p>
                        {q.cite && <cite>- {q.cite}</cite>}
                      </blockquote>
                    ))}
                  </div>
                )}

                {!isFoundationPage && hasHeroCol && (data.lead?.title || data.lead?.copy) && (
                  <div className="foundation__aboutText" style={{ marginBottom: "2rem", marginTop: "0", paddingTop: "0" }}>
                    {data.lead?.title && (
                      <h2 className="foundation__heroTitle" style={{ marginTop: "0" }}>{data.lead.title}</h2>
                    )}
                    {data.lead?.eyebrow && (
                      <h3 className="foundation__aboutTitle">{data.lead.eyebrow}</h3>
                    )}
                    {data.lead?.copy && <p>{data.lead.copy}</p>}
                  </div>
                )}

                {data.footerCta?.label && data.footerCta?.href && (
                  <a href={data.footerCta.href} className="foundation__donateButton">
                    {data.footerCta.label}
                  </a>
                )}
              </div>
            )}

            {/* Right: related posts */}
            {/* This section now maps over the hardcoded 'relatedPosts' array */}
            {hasRelated && (
              <div className="foundation__postsColumn reveal-item" style={{ transitionDelay: "240ms" }}>
                <div className="foundation__relatedPosts">
                  <h3 className="foundation__relatedTitle">Related Articles</h3>
                  {relatedPosts.map((post, idx) => (
                    <article key={idx} className="foundation__postCard">
                      <a href={`/blog/${post.slug}`} className="foundation__postImageLink">
                        {post.image && (
                          <img
                            src={post.image}
                            alt={post.title}
                            className="foundation__postImage"
                            loading="lazy"
                          />
                        )}
                      </a>
                      <h4 className="foundation__postTitle">
                        <a href={`/blog/${post.slug}`}>{post.title}</a>
                      </h4>
                      {(post.date || post.readTime) && (
                        <div className="foundation__postMeta">
                          {post.date && (
                            <time className="foundation__postDate" dateTime={post.date}>
                              {fmtDate(post.date)}
                            </time>
                          )}
                          {post.date && post.readTime && <span className="foundation__postSep"> | </span>}
                          {post.readTime && <span className="foundation__postRead">{post.readTime} min read</span>}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== Carousel (optional prop) (unchanged) ===== */}
        {hasCarousel && (
          <div
            ref={carouselRef}
            className={`foundation__carousel-wrap reveal reveal--up ${carouselIn ? "in" : ""}`}
          >
            <div className="foundation__carousel">
              {carouselPhotos.map((photo, i) => (
                <div key={photo.id || i} className="foundation__carousel-card">
                  <img src={photo.src} alt={photo.alt || ""} className="foundation__carousel-image" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CONTENT SECTION ===== */}
        {hasContent && (
          <div
            ref={contentRef}
            className={`foundation__contentWrapper reveal reveal--up ${contentIn ? "in" : ""}`}
          >
            {/* All content blocks rendered with horizontal layout */}
            {extraBlocks.length > 0 && (
              <div className="foundation__paragraph reveal-item" style={{ gridColumn: "1 / -1", transitionDelay: "240ms" }}>
                {extraBlocks.map((b, i) => (
                  <StoryBlock key={i} block={b} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== Donors plaque (unchanged) ===== */}
        {hasDonors && (
          <div className="foundation__donorsPlaque">
            <h3 className="foundation__donorsTitle">{donorsBlock.title || "🙏 Our Donors"}</h3>
            {donorsBlock.copy && <p className="foundation__donorsIntro">{donorsBlock.copy}</p>}
            {Array.isArray(donorsBlock.points) && donorsBlock.points.length > 0 && (
              <ul className="foundation__donorsList">
                {donorsBlock.points.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
            <p className="foundation__donorsFootnote">
              {donorsBlock.eyebrow || "…and many other donors who prefer to remain anonymous."}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// -------- PropTypes --------
const BlockShape = PropTypes.shape({
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  copy: PropTypes.string,
  points: PropTypes.arrayOf(PropTypes.string),
  quote: PropTypes.string,
  cite: PropTypes.string,
  image: PropTypes.any,
  imageAlt: PropTypes.string,
  flip: PropTypes.bool,
});

BlogPage.propTypes = {
  data: PropTypes.shape({
    slug: PropTypes.string,
    hero: PropTypes.shape({
      bgImage: PropTypes.any,
      imagePosition: PropTypes.string,
      bgSize: PropTypes.string,
      kicker: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      dateISO: PropTypes.string,
      readTimeMin: PropTypes.number,
      tags: PropTypes.arrayOf(PropTypes.string),
      author: PropTypes.shape({
        name: PropTypes.string,
        avatar: PropTypes.string,
      }),
    }),
    lead: PropTypes.shape({
      eyebrow: PropTypes.string,
      title: PropTypes.string,
      copy: PropTypes.string,
    }),
    blocks: PropTypes.arrayOf(BlockShape),
    footerCta: PropTypes.shape({
      title: PropTypes.string,
      accent: PropTypes.string,
      copy: PropTypes.string,
      label: PropTypes.string,
      href: PropTypes.string,
    }),
  }).isRequired,
  // Removed relatedPosts from propTypes
  carouselPhotos: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), src: PropTypes.any, alt: PropTypes.string })
  ),
};

